import { FC, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiChip, ZoomInput } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { IAttemptListItemResponseForWorkOrder } from "@miplatform/types";
import { mdiFilterVariant, mdiFormatListChecks } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useCalendarContext } from "../../../providers";
import { CalendarFilterGroup } from "../CalendarToolBar/CalendarFilterGroup";
import {
  matchesCalendarFilters,
  useCalendarFilterGroup,
} from "../CalendarToolBar/useCalendarFilterGroup";
import { getExpectedDurationMinutes } from "../NHCPriorityScheduler/useAssignmentStaging";
import { BulkAssignmentBanner } from "./BulkAssignmentBanner";
import { UnassignedWorkOrdersList } from "./UnassignedWorkOrdersList";

const EMPTY_ATTEMPT_IDS: ReadonlySet<string> = new Set<string>();
const ZOOM_OPTIONS = [1, 2, 3, 4].map((value) => ({
  label: `Zoom ${Math.round(value * 100)}%`,
  value,
}));

export const UnassignedWorkOrders: FC<UnassignedWorkOrdersProps> = ({
  rawUnassignedWorkOrders,
  isWorkOrdersLoading,
  stagedAttemptIds = EMPTY_ATTEMPT_IDS,
  confirmedAttemptIds = EMPTY_ATTEMPT_IDS,
  hasAssignableUserColumn = false,
  remainingDurationForUser = 0,
  remainingDurationByTemplateId,
  onWorkOrderClick,
  onBulkAssign,
}) => {
  const { classes } = useStyles();
  const [isBulkAssignmentMode, setIsBulkAssignmentMode] = useState(false);
  const [checkedWorkOrders, setCheckedWorkOrders] = useState<
    Record<string, IAttemptListItemResponseForWorkOrder>
  >({});
  const toggleBulkAssignmentMode = () => {
    setIsBulkAssignmentMode((prev) => !prev);
    setCheckedWorkOrders({});
  };

  const {
    showFilters,
    toggleFilters,
    filters,
    appliedFilters,
    appliedFilterLabels,
    hasPendingFilters,
    handleApplyFilters,
    handleResetFilters,
    handleFilterChange,
    builderOptions,
    communityOptions,
    statusOptions,
  } = useCalendarFilterGroup();
  const { zoom, setZoom, verticalTabActiveFilter, calendarSet } =
    useCalendarContext();

  const selectedTabLabel = useMemo(() => {
    if (!verticalTabActiveFilter) {
      return null;
    }
    return (
      calendarSet?.config?.workTemplateBases?.find(
        (template) => template.id === verticalTabActiveFilter
      )?.name ?? null
    );
  }, [verticalTabActiveFilter, calendarSet]);

  const workOrders = useMemo(
    () =>
      rawUnassignedWorkOrders.filter((wo) =>
        matchesCalendarFilters(wo, appliedFilters)
      ),
    [rawUnassignedWorkOrders, appliedFilters]
  );

  const visibleWorkOrders = useMemo(
    () =>
      workOrders.filter(
        (wo) =>
          !stagedAttemptIds.has(wo.id) &&
          !confirmedAttemptIds.has(wo.id) &&
          (!verticalTabActiveFilter ||
            wo.workOrderTemplateId === verticalTabActiveFilter)
      ),
    [workOrders, stagedAttemptIds, confirmedAttemptIds, verticalTabActiveFilter]
  );

  const visibleWorkOrdersCount = visibleWorkOrders.length;
  const checkedAttemptIds = useMemo(
    () => new Set(Object.keys(checkedWorkOrders)),
    [checkedWorkOrders]
  );
  const checkedWorkOrdersCount = checkedAttemptIds.size;

  const handleCheckboxChange = (
    attempt: IAttemptListItemResponseForWorkOrder,
    isChecked: boolean
  ) => {
    setCheckedWorkOrders((prev) => {
      if (isChecked) {
        if (prev[attempt.id]) {
          return prev;
        }
        return {
          ...prev,
          [attempt.id]: attempt,
        };
      }
      if (!prev[attempt.id]) {
        return prev;
      }
      const next = { ...prev };
      delete next[attempt.id];

      return next;
    });
  };

  const handleBulkAssign = () => {
    const selectedWorkOrders = Object.values(checkedWorkOrders);
    if (!selectedWorkOrders.length) {
      return;
    }
    onBulkAssign?.(selectedWorkOrders);
    setCheckedWorkOrders({});
  };

  useEffect(() => {
    setCheckedWorkOrders((prev) => {
      if (!Object.keys(prev).length) {
        return prev;
      }
      const visibleIds = new Set(visibleWorkOrders.map((wo) => wo.id));
      let changed = false;
      const next: Record<string, IAttemptListItemResponseForWorkOrder> = {};
      for (const [id, attempt] of Object.entries(prev)) {
        if (visibleIds.has(id)) {
          next[id] = attempt;
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [visibleWorkOrders]);

  const preStagedRemainingByTemplateId = useMemo(() => {
    if (!remainingDurationByTemplateId) {
      return undefined;
    }
    const remaining = { ...remainingDurationByTemplateId };
    for (const wo of Object.values(checkedWorkOrders)) {
      const templateId = wo.workOrderTemplateId;
      if (templateId && remaining[templateId] != null) {
        remaining[templateId] = Math.max(
          0,
          remaining[templateId] - getExpectedDurationMinutes(wo)
        );
      }
    }
    return remaining;
  }, [checkedWorkOrders, remainingDurationByTemplateId]);

  const preStagedDurationForUser = useMemo(() => {
    if (preStagedRemainingByTemplateId) {
      const values = Object.values(preStagedRemainingByTemplateId);
      if (values.length > 0) {
        return values.reduce((sum, v) => sum + v, 0);
      }
    }
    return (
      remainingDurationForUser -
      Object.values(checkedWorkOrders).reduce(
        (acc, curr) => acc + getExpectedDurationMinutes(curr),
        0
      )
    );
  }, [
    checkedWorkOrders,
    remainingDurationForUser,
    preStagedRemainingByTemplateId,
  ]);

  useEffect(() => {
    if (!hasAssignableUserColumn && isBulkAssignmentMode) {
      setIsBulkAssignmentMode(false);
    }
  }, [hasAssignableUserColumn, isBulkAssignmentMode]);

  return (
    <Box className={classes.root}>
      <Box className={classes.topRow}>
        <Box className={classes.titleGroup}>
          <Typography variant={TYPOGRAPHY_VARIANTS.HD2}>
            Unassigned Work Orders
          </Typography>
          {visibleWorkOrdersCount > 0 && (
            <MiChip
              label={String(visibleWorkOrdersCount)}
              size="small"
              className={classes.countChip}
            />
          )}
        </Box>
        <Box className={classes.controlsRow}>
          <Box flex={1}>
            <ZoomInput
              value={zoom}
              options={ZOOM_OPTIONS}
              handleChange={(event: SelectChangeEvent<number>) =>
                setZoom(Number(event.target.value))
              }
              className={classes.zoomInput}
            />
          </Box>
          <MiButton
            title="Bulk Assign"
            onClick={toggleBulkAssignmentMode}
            disabled={!hasAssignableUserColumn}
            startIcon={{
              path: mdiFormatListChecks,
            }}
            variant={BUTTON_VARIANTS.OUTLINED}
            color={BUTTON_COLORS.PRIMARY}
            className={classes.filterButton}
          />

          <MiButton
            title="Filter"
            onClick={toggleFilters}
            startIcon={{
              path: mdiFilterVariant,
            }}
            variant={BUTTON_VARIANTS.OUTLINED}
            color={BUTTON_COLORS.PRIMARY}
            className={classes.filterButton}
          />
        </Box>
      </Box>

      {appliedFilterLabels.length > 0 && (
        <Box className={classes.chipsContainer}>
          {appliedFilterLabels.map((label) => (
            <MiChip
              key={label}
              label={label}
              size="small"
              variant="outlined"
              className={classes.chip}
            />
          ))}
        </Box>
      )}

      {isBulkAssignmentMode && (
        <BulkAssignmentBanner
          onExit={toggleBulkAssignmentMode}
          onAssign={handleBulkAssign}
          assignDisabled={checkedWorkOrdersCount === 0}
          preStagedDuration={preStagedDurationForUser}
          checkedWorkOrdersCount={checkedWorkOrdersCount}
        />
      )}

      {showFilters && (
        <Box className={classes.bottomRow}>
          <CalendarFilterGroup
            filters={filters}
            builderOptions={builderOptions}
            communityOptions={communityOptions}
            statusOptions={statusOptions}
            hasPendingFilters={hasPendingFilters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
            onToggleFilters={toggleFilters}
            handleFilterChange={handleFilterChange}
            layout="stacked"
          />
        </Box>
      )}

      <Box className={classes.listContainer}>
        <UnassignedWorkOrdersList
          workOrders={visibleWorkOrders}
          loading={isWorkOrdersLoading}
          selectedTabLabel={selectedTabLabel}
          onWorkOrderClick={onWorkOrderClick}
          zoom={zoom}
          isBulkAssignmentMode={isBulkAssignmentMode}
          onCheckboxChange={handleCheckboxChange}
          checkedAttemptIds={checkedAttemptIds}
          remainingDurationForUser={
            isBulkAssignmentMode
              ? preStagedDurationForUser
              : remainingDurationForUser
          }
          remainingDurationByTemplateId={
            isBulkAssignmentMode
              ? preStagedRemainingByTemplateId
              : remainingDurationByTemplateId
          }
          hasAssignableUserColumn={hasAssignableUserColumn}
        />
      </Box>
    </Box>
  );
};

interface UnassignedWorkOrdersProps {
  rawUnassignedWorkOrders: IAttemptListItemResponseForWorkOrder[];
  isWorkOrdersLoading: boolean;
  stagedAttemptIds?: ReadonlySet<string>;
  confirmedAttemptIds?: ReadonlySet<string>;
  hasAssignableUserColumn?: boolean;
  remainingDurationForUser?: number;
  remainingDurationByTemplateId?: Record<string, number>;
  onWorkOrderClick?: (attempt: IAttemptListItemResponseForWorkOrder) => void;
  onBulkAssign?: (attempts: IAttemptListItemResponseForWorkOrder[]) => void;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    borderRadius: 4,
    flex: 1,
    overflow: "auto",
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  controlsRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  titleGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  filterButton: {
    height: 24,
    padding: theme.spacing(0, 1),
    flex: 1,
  },
  zoomInput: {
    "& .MuiSelect-select": {
      height: "24px !important",
      fontSize: theme.fontSize.small,
    },
  },
  bottomRow: {
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
  },
  chipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  chip: {
    height: 20,
    fontSize: 11,
    borderRadius: 2,
    backgroundColor: theme.palette.grey.lightAccent,
  },
  countChip: {
    height: 20,
    minWidth: 16,
    fontSize: 11,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.error.main,
  },
  listContainer: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    backgroundColor: theme.palette.blue.light,
    borderRadius: theme.spacing(1),
  },
}));
